import React, { useState, useCallback } from 'react';
import {
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    IconButton,
    Button,
    Text,
    HStack,
    VStack,
    Box,
    Spinner,
    Badge,
    Tooltip,
    useBreakpointValue
} from '@chakra-ui/react';
import {
    MdOutlineDescription,
    MdOutlineFolder,
    MdNoteAdd,
} from 'react-icons/md';
import { useDocuments } from './hooks/useDocuments';
import { fetchWithAuth } from '../utils/fetchWithAuth';

const DocumentQuickAccess = ({
    onDocumentInsert,
    size = 'md',
    variant = 'solid',
    colorScheme = 'gray',
    displayMode = 'icon'
}) => {
    const { documents, processingDocuments } = useDocuments();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const isMobile = useBreakpointValue({ base: true, md: false });

    const availableDocuments = documents.filter(doc =>
        doc.status === 'completed'
    ).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    const processingCount = documents.filter(doc =>
        doc.status === 'processing'
    ).length;

    const handleDocumentSelect = useCallback(async (documentId, filename) => {
        try {
            const response = await fetchWithAuth(`/api/documents/${documentId}`, {
            });
            
            if (!response.ok) {
                throw new Error('Failed to fetch document content');
            }

            const document = await response.json();
            const text = document.edited_text || document.original_text || '';

            const formattedText = `--- Document: ${filename} ---\n${text}\n--- End Document ---\n\n`;

            onDocumentInsert(formattedText);
            setIsMenuOpen(false);

        } catch (error) {
            console.error('Error inserting document:', error);
        }
    }, [onDocumentInsert]);

    const getStatusBadge = (status) => {
        switch (status) {
            case 'processing':
                return <Badge colorScheme="blue" size="sm">Processing</Badge>;
            case 'completed':
                return <Badge colorScheme="green" size="sm">Ready</Badge>;
            case 'failed':
                return <Badge colorScheme="red" size="sm">Failed</Badge>;
            default:
                return null;
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const buttonProps = {
        as: displayMode === 'icon' ? IconButton : Button,
        ...(displayMode === 'icon' ? {
            icon: <MdNoteAdd size="20px" />,
            isRound: true,
            minW: "48px"
        } : {
            leftIcon: <MdNoteAdd />,
            children: 'Documents'
        }),
        variant,
        colorScheme,
        size,
        h: "48px",
        'aria-label': "Insert document",
        title: "Insert document content"
    };

    return (
        <Menu
            isOpen={isMenuOpen}
            onOpen={() => setIsMenuOpen(true)}
            onClose={() => setIsMenuOpen(false)}
            closeOnSelect={false}
        >
            <Tooltip label="Insert document content" hasArrow>
                <MenuButton {...buttonProps} />
            </Tooltip>

            <MenuList
                maxW="400px"
                maxH="400px"
                overflowY="auto"
                border="1px"
                borderColor="gray.200"
                shadow="lg"
                zIndex={1000}
            >
                <MenuItem
                    isDisabled
                    fontSize="xs"
                    fontWeight="600"
                    color="gray.600"
                    bg="gray.50"
                    _hover={{ bg: 'gray.50' }}
                >
                    <HStack justify="space-between" w="full">
                        <Text>DOCUMENTS</Text>
                        {processingCount > 0 && (
                            <HStack spacing={1}>
                                <Spinner size="xs" />
                                <Text>{processingCount} processing</Text>
                            </HStack>
                        )}
                    </HStack>
                </MenuItem>

                {availableDocuments.length > 0 ? (
                    availableDocuments.map((doc) => (
                        <MenuItem
                            key={doc.id}
                            onClick={() => handleDocumentSelect(doc.id, doc.filename)}
                            py={3}
                            _hover={{ bg: 'gray.50' }}
                        >
                            <HStack spacing={3} w="full" align="flex-start">
                                <MdOutlineFolder
                                    size="18px"
                                    color="gray.600"
                                    style={{ marginTop: '2px', flexShrink: 0 }}
                                />
                                <VStack align="flex-start" spacing={1} flex="1" minW="0">
                                    <HStack justify="space-between" w="full">
                                        <Text
                                            fontWeight="500"
                                            fontSize="sm"
                                            isTruncated
                                            flex="1"
                                        >
                                            {doc.filename}
                                        </Text>
                                        {getStatusBadge(doc.status)}
                                    </HStack>
                                    <HStack spacing={4}>
                                        <Text fontSize="xs" color="gray.500">
                                            {formatFileSize(doc.file_size)}
                                        </Text>
                                        <Text fontSize="xs" color="gray.500">
                                            {doc.ocr_tier === 'advanced' ? 'Advanced OCR' : 'Standard OCR'}
                                        </Text>
                                    </HStack>
                                </VStack>
                            </HStack>
                        </MenuItem>
                    ))
                ) : (
                    <MenuItem isDisabled py={4}>
                        <VStack spacing={2}>
                            <Text fontSize="sm" color="gray.500">
                                No documents available
                            </Text>
                            <Text fontSize="xs" color="gray.400">
                                Upload PDFs from the Documents page
                            </Text>
                        </VStack>
                    </MenuItem>
                )}

                {documents.length > 0 && (
                    <MenuItem
                        as="a"
                        href="/documents"
                        py={2}
                        fontSize="xs"
                        color="brand.600"
                        _hover={{ bg: 'brand.50' }}
                        borderTop="1px"
                        borderColor="gray.100"
                    >
                        <HStack spacing={2}>
                            <MdOutlineFolder size="14px" />
                            <Text>Manage Documents</Text>
                        </HStack>
                    </MenuItem>
                )}
            </MenuList>
        </Menu>
    );
};

export default DocumentQuickAccess;